import { z } from "zod";

/** Light, dark, or whatever the shopper's device is set to. */
export const CHAT_SCHEMES = ["light", "dark", "auto"] as const;
export type ChatScheme = (typeof CHAT_SCHEMES)[number];

/** How round bubbles, cards and tiles are. Icon buttons stay circles. */
export const CHAT_CORNERS = ["round", "soft", "square"] as const;
export type ChatCorners = (typeof CHAT_CORNERS)[number];

/** The chat header on the page's surface, or filled with the brand colour. */
export const CHAT_HEADERS = ["plain", "brand"] as const;
export type ChatHeader = (typeof CHAT_HEADERS)[number];

/** The shape of buttons: the send and mic buttons, add, view basket, checkout. */
export const CHAT_BUTTONS = ["pill", "rounded", "square"] as const;
export type ChatButtons = (typeof CHAT_BUTTONS)[number];

/** The shopper's own messages: filled with the brand colour, or a tint of it. */
export const CHAT_BUBBLES = ["brand", "soft"] as const;
export type ChatBubble = (typeof CHAT_BUBBLES)[number];

/** Typefaces the chat ships with; `system` is the device's own. */
export const CHAT_FONTS = ["geist", "manrope", "system"] as const;
export type ChatFont = (typeof CHAT_FONTS)[number];

/** Starter prompts as tiles with catalog photos, or a plain list. */
export const CHAT_STARTER_STYLES = ["photos", "list"] as const;

/** `#RRGGBB`. The value is written into CSS, so nothing else gets through. */
export const hexColorSchema = z
	.string()
	.regex(/^#[0-9a-fA-F]{6}$/, "Use a 6-digit hex colour like #1E1EF2");

/**
 * A typeface by name. The value is written into a CSS font-family, so it may not hold quotes,
 * semicolons or brackets.
 */
export const fontFamilySchema = z
	.string()
	.trim()
	.min(1, "Name the typeface")
	.max(40, "Keep the name under 40 characters")
	.regex(
		/^[\p{L}\p{N} ._-]+$/u,
		"Use letters, digits, spaces, dots, dashes or underscores",
	);

/** A bad value is dropped, the rest still apply: it comes from someone else's app. */
const optional = <T extends z.ZodType>(schema: T) =>
	schema.optional().catch(undefined);

/** `showBasket=false` in a URL reaches the schema as a boolean (the router reads JSON). */
const flag = optional(z.boolean());

/**
 * What a retailer's app or site may change in the chat it opens (ADR-5.1, 5.4): search params
 * of `/c/<orgSlug>` for the first paint, and the data of the `appearance` bridge message for
 * changes after it. Looks and what is shown, never words: the URL is unsigned, so text goes
 * only over the bridge (`chatTextSchema`). The page applies them over the retailer's published
 * settings while `appearance.allowAppOverrides` is on.
 */
export const appearanceOverridesSchema = z.object({
	scheme: optional(z.enum(CHAT_SCHEMES)),
	primaryColor: optional(hexColorSchema),
	deepColor: optional(hexColorSchema),
	accentColor: optional(hexColorSchema),
	dealColor: optional(hexColorSchema),
	corners: optional(z.enum(CHAT_CORNERS)),
	header: optional(z.enum(CHAT_HEADERS)),
	/** One part's own colour; the text on it is picked for contrast. */
	ctaColor: optional(hexColorSchema),
	headerColor: optional(hexColorSchema),
	bubbleColor: optional(hexColorSchema),
	surfaceColor: optional(hexColorSchema),
	/** Cards and sheets, text, secondary text, lines and borders. */
	cardColor: optional(hexColorSchema),
	textColor: optional(hexColorSchema),
	mutedColor: optional(hexColorSchema),
	lineColor: optional(hexColorSchema),
	buttons: optional(z.enum(CHAT_BUTTONS)),
	bubble: optional(z.enum(CHAT_BUBBLES)),
	font: optional(z.enum(CHAT_FONTS)),
	/**
	 * A typeface the device already has ("Avenir Next", "Roboto"), ahead of `font`: the chat
	 * reads like the app around it. A name the device lacks falls back to `font`.
	 */
	fontFamily: optional(fontFamilySchema),
	starterStyle: optional(z.enum(CHAT_STARTER_STYLES)),
	/** What is shown. An app with its own navigation bar or cart hides the chat's. */
	showHeader: flag,
	showLogo: flag,
	/** The basket as a whole: its button, its bar and the basket itself. */
	showBasket: flag,
	/** One part of it each: the header's button, the bar with the total. */
	showBasketButton: flag,
	showBasketBar: flag,
	showCheckout: flag,
	showNewChat: flag,
	showFollowUps: flag,
	showSteps: flag,
	/** Tapping a product opens the chat's own sheet (the host always gets `open_product`). */
	productSheet: flag,
	/** The + on product cards. */
	addFromCards: flag,
	/** The assistant's name above each reply. */
	showAssistantName: flag,
	/** The suggested first messages on the welcome screen. */
	showStarters: flag,
	/**
	 * The welcome screen's title, line and suggestions. Off when the app draws its own above
	 * the chat and sends what the shopper picks with `chat.send` (since SDK 2.2.0).
	 */
	showWelcome: flag,
	/** A back arrow before the logo that posts `close`: for a chat opened as a page (2.2.0). */
	showBack: flag,
	/** "Powered by Hephon", when Hephon allows the retailer to hide it (2.2.0). */
	showPoweredBy: flag,
	/**
	 * Named `hide` because that is all an app can do with them: what the retailer has
	 * switched off stays off. "Help with an order", the microphone (voice messages and
	 * dictation), the live call.
	 */
	hideOrderHelp: flag,
	hideVoice: flag,
	hideCall: flag,
});
export type AppearanceOverrides = z.infer<typeof appearanceOverridesSchema>;

/** The buttons and titles whose wording a retailer or its app may replace. */
export const CHAT_LABELS = [
	"viewBasket",
	"basketTitle",
	"checkout",
	"addToBasket",
	"orderHelp",
	"newChat",
	"total",
	"emptyBasket",
	"thinking",
	"typing",
	"tryAgain",
] as const;
export type ChatLabel = (typeof CHAT_LABELS)[number];

const words = (max: number) => optional(z.string().trim().min(1).max(max));

/**
 * Wording an app may replace, in the data of the `text` bridge message. Bridge only: a host
 * is the retailer's own app or one of its allowed origins, a URL is anyone's to craft.
 */
export const chatTextSchema = z.object({
	welcomeTitle: words(80),
	welcomeSubtitle: words(140),
	placeholder: words(60),
	tagline: words(60),
	/** The name in the header and above each reply. */
	assistantName: words(40),
	labels: optional(
		z.object(
			Object.fromEntries(
				CHAT_LABELS.map((label) => [label, words(40)]),
			) as Record<ChatLabel, ReturnType<typeof words>>,
		),
	),
});
export type ChatText = z.infer<typeof chatTextSchema>;

export const APPEARANCE_OVERRIDE_KEYS = Object.keys(
	appearanceOverridesSchema.shape,
) as (keyof AppearanceOverrides)[];

/** The overrides that are set, or undefined when there are none. */
export function pickAppearanceOverrides(
	source: AppearanceOverrides,
): AppearanceOverrides | undefined {
	const set = Object.fromEntries(
		APPEARANCE_OVERRIDE_KEYS.flatMap((key) =>
			source[key] === undefined ? [] : [[key, source[key]]],
		),
	) as AppearanceOverrides;
	return Object.keys(set).length > 0 ? set : undefined;
}
